// These keep the password manager working on the sign-in and password screens.
const CREDENTIAL_TOKENS = new Set(['username', 'current-password', 'new-password', 'one-time-code']);
const FIELD_SELECTOR = 'input, textarea, select';

function applyAutofillPolicy(field: Element): void {
  const current = field.getAttribute('autocomplete')?.trim().toLowerCase() ?? '';
  if (current.split(/\s+/).some((token) => CREDENTIAL_TOKENS.has(token))) return;
  // Chrome ignores "off" on password inputs and offers saved logins; "new-password" is what stops it.
  const wanted = field.getAttribute('type') === 'password' ? 'new-password' : 'off';
  if (current !== wanted) field.setAttribute('autocomplete', wanted);
}

/**
 * Stops the browser suggesting previously typed values on every field in the app, including ones
 * rendered later (lazy routes, dialogs, overlays), so templates do not each have to remember it.
 * To let a field keep password-manager fill, give it one of the CREDENTIAL_TOKENS.
 */
export function disableBrowserAutofill(root: Document = document): MutationObserver {
  root.querySelectorAll(FIELD_SELECTOR).forEach(applyAutofillPolicy);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if ((mutation.target as Element).matches(FIELD_SELECTOR)) applyAutofillPolicy(mutation.target as Element);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const element = node as Element;
        if (element.matches(FIELD_SELECTOR)) applyAutofillPolicy(element);
        element.querySelectorAll(FIELD_SELECTOR).forEach(applyAutofillPolicy);
      });
    }
  });
  // `type` too: show/hide-password toggles swap it on a live input.
  observer.observe(root.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['autocomplete', 'type'],
  });
  return observer;
}
